/**
 * Aba "Solicitações" do Painel de Assistências — a lista por trás dos números
 * em formato de tabela: o gestor escolhe QUAIS colunas ver, filtra cada coluna
 * no padrão do Excel e salva a combinação como a sua visualização (preferência
 * do usuário, vale em qualquer dispositivo). Substitui o painel lateral que
 * abria ao clicar num KPI: os KPIs agora trazem para cá, já na lista certa.
 *
 * O recorte do topo da página (período, tipos, filtros) define o universo; os
 * filtros daqui refinam dentro dele. Exporta o que está filtrado, com as
 * colunas visíveis.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiColumns, FiRotateCcw } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import ExportMenu from "../../../components/ExportMenu";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { useTabelaPreferenciaUI } from "../../../hooks/useTabelaPreferencia";
import { listarItensPainel } from "../../../services/solicitacoes";
import { montarTemaExportAtual, salvarCSV, salvarExcel, salvarImagem, salvarPDF } from "../../../utils/exportacao";
import { Carregando } from "../_shared/Estados";
import { Card } from "./_blocos";
import {
  CHAVE_PREFERENCIA, COLUNAS, COLUNAS_PADRAO, CONJUNTOS, assinatura, colunaDe, dePreferencia,
  normalizarColunas, opcoesDaColuna, ordenarLinhas, paraPreferencia, passaNosFiltros, proximaOrdenacao,
} from "./colunasSolicitacoes";
import TabelaSolicitacoes from "./TabelaSolicitacoes";

const BLOCO = 200;
const POR_PAGINA = 100;
const SAVERS = { pdf: salvarPDF, excel: salvarExcel, csv: salvarCSV };
// Imagem é a tabela como está na tela; acima disso o canvas do navegador não
// aguenta e o arquivo sairia em branco (o exportador recusa).
// ~80px por linha na captura 2x e o exportador recusa acima de 16.000px de
// altura: até LINHAS_EM_ALTA sai em 2x; daí até o máximo, em 1x.
const LINHAS_EM_ALTA = 150;
const MAX_LINHAS_IMAGEM = 350;

async function carregarTudo(request, recorte, conjunto) {
  const primeira = await listarItensPainel(request, recorte, { conjunto, page: 1, page_size: BLOCO });
  const linhas = [...(primeira.results || [])];
  const paginas = Math.ceil((primeira.count || 0) / BLOCO);
  for (let p = 2; p <= paginas; p += 1) {
    // eslint-disable-next-line no-await-in-loop
    const r = await listarItensPainel(request, recorte, { conjunto, page: p, page_size: BLOCO });
    linhas.push(...(r.results || []));
  }
  return linhas;
}

function SeletorColunas({ visiveis, onChange }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => { if (!ref.current?.contains(e.target)) setAberto(false); };
    const esc = (e) => e.key === "Escape" && setAberto(false);
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", fora); document.removeEventListener("keydown", esc); };
  }, [aberto]);

  const set = new Set(visiveis);
  const alternar = (key) => {
    const prox = new Set(set);
    if (prox.has(key)) prox.delete(key); else prox.add(key);
    onChange(normalizarColunas([...prox]));
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" className="btn btn--ghost" aria-expanded={aberto} onClick={() => setAberto((v) => !v)}>
        <FiColumns size={14} /> Colunas <span className="muted tiny">{visiveis.length}/{COLUNAS.length}</span>
      </button>
      {aberto && (
        <div role="dialog" aria-label="Colunas visíveis"
             style={{
               position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 30, width: 280, maxHeight: 380,
               overflowY: "auto", padding: 10, background: "var(--surface)", border: "1px solid var(--border)",
               borderRadius: 12, boxShadow: "var(--shadow-2, 0 12px 32px rgba(0,0,0,.28))",
             }}>
          <div className="flex between" style={{ marginBottom: 6 }}>
            <strong style={{ fontSize: 12.5 }}>Colunas visíveis</strong>
            <button type="button" className="btn btn--ghost" style={{ height: 24, fontSize: 11.5 }}
                    onClick={() => onChange(normalizarColunas(COLUNAS_PADRAO))}>
              Padrão
            </button>
          </div>
          {COLUNAS.map((c) => (
            <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px", fontSize: 12.5, cursor: c.fixa ? "default" : "pointer", color: "var(--ink)" }}>
              <input type="checkbox" checked={set.has(c.key)} disabled={c.fixa} onChange={() => alternar(c.key)} />
              {c.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AbaSolicitacoes({ recorte, conjunto, onConjunto, periodo, onAbrir, chegadaPorKpi = 0 }) {
  const { request } = useAuth();
  const toast = useToast();
  const tabelaRef = useRef(null);
  const [ordenacao, setOrdenacao] = useState(null);
  const [tudoNaTela, setTudoNaTela] = useState(false);
  const { getUI, setUI, flush, estado: estadoPref, ui } = useTabelaPreferenciaUI();
  const [linhas, setLinhas] = useState(null);
  const [erro, setErro] = useState("");
  const [colunas, setColunas] = useState(() => normalizarColunas(COLUNAS_PADRAO));
  const [filtros, setFiltros] = useState({});
  const [pagina, setPagina] = useState(1);
  const mexeu = useRef(false);
  const hidratou = useRef(false);

  // Aplica a visualização salva UMA vez, quando a preferência termina de
  // carregar — sem atropelar o que a pessoa já mexeu enquanto carregava.
  useEffect(() => {
    if (hidratou.current || estadoPref === "loading") return;
    hidratou.current = true;
    if (estadoPref !== "pronto" || mexeu.current) return;
    const salva = getUI()?.[CHAVE_PREFERENCIA];
    if (!salva) return;
    const v = dePreferencia(salva);
    setColunas(v.colunas);
    setOrdenacao(v.ordenacao);
    // Veio de um KPI: o número clicado tem de bater com a lista, então os
    // filtros salvos ficam de fora (as colunas salvas valem sempre).
    if (!chegadaPorKpi) setFiltros(v.filtros);
  }, [estadoPref, getUI, chegadaPorKpi]);

  useEffect(() => {
    if (!chegadaPorKpi) return;
    setPagina(1);
    setFiltros({});
  }, [chegadaPorKpi]);

  const pedido = useRef(0);
  useEffect(() => {
    pedido.current += 1;
    const meu = pedido.current;
    setLinhas(null);
    setErro("");
    carregarTudo(request, recorte, conjunto)
      .then((l) => { if (pedido.current === meu) setLinhas(l); })
      .catch((e) => { if (pedido.current === meu) setErro(e?.message || "Erro ao carregar a lista."); });
  }, [request, recorte, conjunto]);

  const mudarColunas = (c) => { mexeu.current = true; setColunas(c); };
  const mudarFiltro = useCallback((key, set) => {
    mexeu.current = true;
    setPagina(1);
    setFiltros((f) => ({ ...f, [key]: set }));
  }, []);
  const limparFiltros = () => { mexeu.current = true; setPagina(1); setFiltros({}); };

  const visiveis = useMemo(() => colunas.map(colunaDe).filter(Boolean), [colunas]);
  const filtradas = useMemo(
    () => ordenarLinhas((linhas || []).filter((l) => passaNosFiltros(l, filtros)), ordenacao),
    [linhas, filtros, ordenacao],
  );
  const ordenarPor = (key) => { mexeu.current = true; setPagina(1); setOrdenacao((o) => proximaOrdenacao(o, key)); };
  const temFiltro = Object.values(filtros).some(Boolean);
  const paginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
  const daPagina = tudoNaTela
    ? filtradas
    : filtradas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  const atual = { colunas, filtros, ordenacao };
  const salva = ui?.[CHAVE_PREFERENCIA];
  const referencia = salva ? dePreferencia(salva) : { colunas: normalizarColunas(COLUNAS_PADRAO), filtros: {}, ordenacao: null };
  const mudou = hidratou.current && assinatura(atual) !== assinatura(referencia);

  const salvarVisualizacao = async () => {
    setUI({ [CHAVE_PREFERENCIA]: paraPreferencia(atual) });
    await flush({ propagarErro: true });
  };

  const tituloLista = CONJUNTOS.find((c) => c.value === conjunto)?.label || "Solicitações";

  const exportarImagem = async (formato) => {
    if (filtradas.length > MAX_LINHAS_IMAGEM) {
      toast.error(`Imagem comporta até ${MAX_LINHAS_IMAGEM} linhas; filtre a lista ou use PDF/Excel.`);
      return;
    }
    try {
      await salvarImagem({
        titulo: `Painel de Assistências — ${tituloLista}`,
        formato,
        scale: filtradas.length > LINHAS_EM_ALTA ? 1 : 2,
        elements: [tabelaRef.current],
        // Cores do tema já em HEX: o html2canvas não entende `var(--…)`.
        tema: montarTemaExportAtual(),
        expandHooks: {
          // A imagem leva TODAS as linhas filtradas, não só a página visível.
          beforeCapture: () => new Promise((ok) => { setTudoNaTela(true); setTimeout(ok, 120); }),
          afterCapture: () => setTudoNaTela(false),
        },
      });
    } catch (e) {
      // html2canvas às vezes rejeita com STRING, não com Error.
      const motivo = typeof e === "string" ? e : e?.message;
      toast.error(motivo ? `Não foi possível gerar a imagem: ${motivo}` : "Não foi possível gerar a imagem.");
    }
  };

  const exportar = (formato) => {
    if (formato === "png" || formato === "jpg") { exportarImagem(formato); return; }
    const saver = SAVERS[formato];
    if (!saver) return;
    saver({
      titulo: `Painel de Assistências — ${tituloLista}`,
      resumo: [
        { label: "Solicitações", value: filtradas.length, type: "number" },
        ...(periodo && conjunto === "recorte" ? [{ label: "Período", value: periodo, type: "text" }] : []),
      ],
      secoes: [{
        id: "solicitacoes",
        label: tituloLista,
        columns: visiveis.map((c) => ({ key: c.key, label: c.label, type: c.tipoExport || "text" })),
        rows: filtradas.map((l) => Object.fromEntries(
          visiveis.map((c) => [c.key, c.bruto ? c.bruto(l) : c.valor(l)]),
        )),
      }],
    });
  };

  return (
    <div className="widget-grid">
    <Card
      titulo="Solicitações"
      span={12}
      ajuda="Todas as solicitações da lista escolhida, uma por linha. Em Colunas você escolhe quais dados aparecem; clique no nome da coluna para ordenar (A→Z, Z→A), no funil para filtrar como no Excel e arraste a borda para ajustar a largura; Salvar visualização guarda colunas e filtros para as próximas vezes. Clique numa linha para abrir a solicitação na Gestão."
      acao={(
        <div className="flex" style={{ gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {mudou && (
            <AsyncButton variant="secondary" onClick={salvarVisualizacao} successMessage="Visualização salva">
              Salvar visualização
            </AsyncButton>
          )}
          <SeletorColunas visiveis={colunas} onChange={mudarColunas} />
          <ExportMenu onExport={exportar} disabled={filtradas.length === 0} comImagem />
        </div>
      )}
    >
      <div className="flex" style={{ gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <select className="input" aria-label="Lista" value={conjunto} onChange={(e) => { setPagina(1); onConjunto(e.target.value); }}
                style={{ height: 32, maxWidth: 240 }}>
          {CONJUNTOS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        {temFiltro && (
          <button type="button" className="btn btn--ghost" onClick={limparFiltros}>
            <FiRotateCcw size={13} /> Limpar filtros
          </button>
        )}
        <span className="muted tiny" style={{ marginLeft: "auto" }}>
          {linhas ? `${filtradas.length} de ${linhas.length}` : ""}
          {conjunto !== "recorte" ? " · fila de agora (não segue o período)" : ""}
        </span>
      </div>

      {erro && <div className="empty"><div className="msg">{erro}</div></div>}
      {!erro && !linhas && <Carregando />}
      {!erro && linhas && (
        <>
          <TabelaSolicitacoes
            colunas={visiveis}
            linhas={daPagina}
            filtros={filtros}
            ordenacao={ordenacao}
            opcoesDe={(c) => opcoesDaColuna(c, linhas, filtros)}
            onFiltro={mudarFiltro}
            onOrdenar={ordenarPor}
            onLinha={(l) => onAbrir(l.numero)}
            vazio={temFiltro ? "Nada passa nos filtros atuais." : "Nada nesta lista."}
            tabelaRef={tabelaRef}
          />
          {paginas > 1 && !tudoNaTela && (
            <div className="flex" style={{ gap: 8, alignItems: "center", justifyContent: "flex-end", marginTop: 26 }}>
              <span className="muted tiny">página {pagina} de {paginas}</span>
              <button type="button" className="btn btn--ghost" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>Anterior</button>
              <button type="button" className="btn btn--ghost" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}>Próxima</button>
            </div>
          )}
        </>
      )}
    </Card>
    </div>
  );
}
